import { useState, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Save, Trash2, MapPin } from "lucide-react";

const ROLES = ["FOUNDER","BDOM","PCWM","GEHM","SMCM"];
const ROLE_LABEL = { FOUNDER:"Founder", BDOM:"BDOM", PCWM:"PCWM", GEHM:"GEHM", SMCM:"SMCM" };
const DEFAULT_COUNTRIES = ["India", "Nepal", "Sri Lanka", "South Africa", "Kenya", "Thailand", "Vietnam", "Uganda", "Nigeria", "Bolivia"];

export default function TaskEditModal({ task, user, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task.title || "",
    description: task.description || "",
    priority: task.priority || "medium",
    due_date: task.due_date || "",
    assignee_role: task.assignee_role,
    assignee_user_id: task.assignee_user_id || "",
    destination: task.destination || "",
    city: task.city || "",
  });
  const [team, setTeam] = useState([]);
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then(r => setTeam(r.data.filter(x => x.role !== "FOUNDER")));
    api.get("/accommodations/countries").catch(() => ({ data: { countries: [] } })).then(r => {
      const list = r.data?.countries;
      if (Array.isArray(list) && list.length) setCountries(list);
    });
  }, []);

  const canReassign = ["FOUNDER","BDOM"].includes(user.effective_role);
  const canRetarget = ["FOUNDER","BDOM"].includes(user.effective_role);
  const canDelete = ["FOUNDER","BDOM"].includes(user.effective_role);
  const assignableRoles = user.effective_role === "FOUNDER" ? ROLES
    : user.effective_role === "BDOM" ? ["BDOM","PCWM","GEHM","SMCM"]
    : [user.effective_role];

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        title: form.title, description: form.description,
        priority: form.priority, due_date: form.due_date || null,
      };
      if (canReassign) {
        patch.assignee_role = form.assignee_role;
        patch.assignee_user_id = form.assignee_user_id || null;
      }
      if (canRetarget) {
        patch.destination = form.destination || "";
        patch.city = form.city || "";
      }
      await api.patch(`/tasks/${task.id}`, patch);
      toast.success("Task updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${task.id}`);
    onSaved?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="task-edit-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-lg">Edit task</h3>
            <button onClick={onClose}><X size={18}/></button>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Title</label>
            <input data-testid="edit-title" value={form.title} onChange={e=>setForm(v=>({...v,title:e.target.value}))} className="input-aii"/>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Description</label>
            <textarea rows={3} value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))} className="input-aii"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Priority</label>
              <select data-testid="edit-priority" value={form.priority} onChange={e=>setForm(v=>({...v,priority:e.target.value}))} className="input-aii">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Due date</label>
              <input type="date" value={form.due_date||""} onChange={e=>setForm(v=>({...v,due_date:e.target.value}))} className="input-aii"/>
            </div>
          </div>
          {canReassign && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Assign role</label>
                <select data-testid="edit-role" value={form.assignee_role} onChange={e=>setForm(v=>({...v,assignee_role:e.target.value, assignee_user_id:""}))} className="input-aii">
                  {assignableRoles.map(r=><option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">Specific person</label>
                <select value={form.assignee_user_id||""} onChange={e=>setForm(v=>({...v,assignee_user_id:e.target.value}))} className="input-aii">
                  <option value="">Anyone in {ROLE_LABEL[form.assignee_role]}</option>
                  {team.filter(u => u.role === form.assignee_role).map(u=><option key={u.id} value={u.id}>{u.name}{u.destination ? ` · ${u.destination}` : ""}</option>)}
                </select>
              </div>
            </div>
          )}
          {canRetarget && (
            <div className="border-t border-[color:var(--aii-border)] pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)] mb-2 flex items-center gap-1">
                <MapPin size={11}/> Route to office
              </div>
              <p className="text-[11px] text-[color:var(--aii-muted)] mb-2">Retarget this task across countries without changing the assignee&apos;s own profile. Leave blank to make it visible to everyone in that role.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-[color:var(--aii-text-2)]">Destination</label>
                  <select data-testid="edit-destination" value={form.destination||""} onChange={e=>setForm(v=>({...v,destination:e.target.value}))} className="input-aii">
                    <option value="">— Any / unscoped —</option>
                    {countries.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-[color:var(--aii-text-2)]">City</label>
                  <input data-testid="edit-city" value={form.city||""} onChange={e=>setForm(v=>({...v,city:e.target.value}))} placeholder="e.g. Jaipur" className="input-aii"/>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center pt-2">
            {canDelete && (
              <button onClick={del} className="text-red-600 text-sm font-semibold hover:underline flex items-center gap-1"><Trash2 size={14}/> Delete</button>
            )}
            <div className="ml-auto flex gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
              <button data-testid="edit-save" onClick={save} disabled={busy} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5"><Save size={14}/> Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
